// src/lib/attribute_schema.ts — schema atributelor unui strat (`config.attribute_schema`), PUR,
// partajat server↔client (decizia 1.6, BD-020/MOB-023: LISTE DE VALORI per atribut).
//
// Două forme coexistă istoric și amândouă rămân valide:
//   veche: { cheie: "String" }                       (tipul GDAL ca text — importul clasic)
//   nouă:  { cheie: { type: "String", values?: [...] } } (F9-T5 + acum listele de valori)
// `values` = lista ÎNCHISĂ de valori permise pentru atribut: formularele (editare/colectare) randează
// un <select>, iar serverul RESPINGE la create/PATCH feature o valoare din afara listei (fail-closed).
// Valoarea GOALĂ (null / "" / lipsă) trece — lista constrânge ce se completează, nu obligă completarea
// (obligativitatea e altă poveste). Importul NU validează (datele istorice pot conține orice).

export const MAX_LIST_VALUES = 200

export type ListValue = string | number

export interface AttributeField {
	type: string // tipul declarat ("String", "Integer", "Real"…), "" dacă lipsește
	values: ListValue[] | null // lista de valori permise; null = liber
}

import type { Validation } from "./validation"

function is_obj(x: unknown): x is Record<string, unknown> {
	return typeof x === "object" && x !== null && !Array.isArray(x)
}

/** Câmpul `key` din schemă (oricare formă) sau null dacă nu e declarat. */
export function schema_field(schema: unknown, key: string): AttributeField | null {
	if (!is_obj(schema) || !(key in schema)) {
		return null
	}
	const raw = schema[key]
	if (typeof raw === "string") {
		return { type: raw, values: null }
	}
	if (!is_obj(raw)) {
		return null
	}
	const values = Array.isArray(raw.values)
		? raw.values.filter(
				(v): v is ListValue =>
					(typeof v === "string" && v !== "") || (typeof v === "number" && Number.isFinite(v)),
			)
		: null
	return {
		type: typeof raw.type === "string" ? raw.type : "",
		values: values && values.length > 0 ? values : null,
	}
}

/** Lista de valori permise a atributului, sau null dacă e liber / nedeclarat. */
export function allowed_values(schema: unknown, key: string): ListValue[] | null {
	return schema_field(schema, key)?.values ?? null
}

/**
 * Valoarea `v` e acceptată de lista dată? Gol (null/undefined/"") = da (nesetat); altfel potrivire
 * strictă SAU pe forma text (un „12" tastat într-un formular vs. 12 numeric din listă — aceeași valoare).
 */
export function value_in_list(values: readonly ListValue[], v: unknown): boolean {
	if (v === null || v === undefined || v === "") {
		return true
	}
	const s = typeof v === "object" ? JSON.stringify(v) : String(v)
	return values.some((x) => x === v || String(x) === s)
}

/** Lista redată în mesajul de eroare — scurtată după 8 valori (o listă de 200 nu încape în toast). */
function list_preview(values: readonly ListValue[]): string {
	const shown = values.slice(0, 8).map(String).join(", ")
	return values.length > 8 ? `${shown}, … (${values.length} valori)` : shown
}

/**
 * Verifică proprietățile unui feature contra listelor din schemă. Întoarce mesajul PRIMEI abateri
 * (RO, cu valorile permise) sau "" dacă totul e în regulă. Atributele fără listă / nedeclarate trec.
 */
export function value_list_problem(schema: unknown, props: Record<string, unknown>): string {
	if (!is_obj(schema)) {
		return ""
	}
	for (const [key, v] of Object.entries(props)) {
		const values = allowed_values(schema, key)
		if (values && !value_in_list(values, v)) {
			return `Valoarea '${String(v)}' pentru atributul '${key}' nu e în lista permisă (${list_preview(values)}).`
		}
	}
	return ""
}

/**
 * Validare FAIL-CLOSED a unei scheme la scriere (create/PATCH layer): obiect; fiecare câmp text (forma
 * veche) sau obiect (forma nouă); `values`, dacă există, = listă de texte nevide / numere finite,
 * fără dubluri (pe forma text), max 200. Textele se curăță de spații. Restul cheilor câmpului trec neatinse.
 */
export function validate_attribute_schema(schema: unknown): Validation<Record<string, unknown>> {
	if (schema === null || schema === undefined) {
		return { ok: true, value: {} }
	}
	if (!is_obj(schema)) {
		return {
			ok: false,
			error: "attribute_schema trebuie să fie un obiect {cheie: tip | {type, values}}",
		}
	}
	const out: Record<string, unknown> = {}
	for (const [key, raw] of Object.entries(schema)) {
		if (typeof raw === "string") {
			out[key] = raw
			continue
		}
		if (!is_obj(raw)) {
			return { ok: false, error: `atributul '${key}': descrierea trebuie să fie text sau obiect` }
		}
		if (raw.values === undefined || raw.values === null) {
			const { values: _drop, ...rest } = raw
			out[key] = rest
			continue
		}
		if (!Array.isArray(raw.values)) {
			return { ok: false, error: `atributul '${key}': values trebuie să fie o listă` }
		}
		if (raw.values.length > MAX_LIST_VALUES) {
			return { ok: false, error: `atributul '${key}': cel mult ${MAX_LIST_VALUES} valori în listă` }
		}
		const values: ListValue[] = []
		const seen = new Set<string>()
		for (const v of raw.values) {
			const clean =
				typeof v === "string" ? v.trim() : typeof v === "number" && Number.isFinite(v) ? v : null
			if (clean === null || clean === "") {
				return {
					ok: false,
					error: `atributul '${key}': lista acceptă doar texte nevide sau numere (găsit: ${JSON.stringify(v)})`,
				}
			}
			if (seen.has(String(clean))) {
				return {
					ok: false,
					error: `atributul '${key}': valoarea '${String(clean)}' se repetă în listă`,
				}
			}
			seen.add(String(clean))
			values.push(clean)
		}
		out[key] = values.length > 0 ? { ...raw, values } : (({ values: _v, ...rest }) => rest)(raw)
	}
	return { ok: true, value: out }
}
